import { IDatetime } from '@/Framework/dateHelpers/Datetime';
import ISector from '@/finsight/domain/vo/ISector';
import ISubsector from '@/finsight/domain/vo/ISubsector';
import IIndustry from '@/finsight/domain/vo/IIndustry';
import ISubindustry from '@/finsight/domain/vo/ISubindustry';
import ITranch, { ITrancheSize } from '@/finsight/domain/vo/Tranch/ITranch';

export interface IProduct {
  id: string,
  name: string,
  displayName: string,
  marketDataName: string,
  abbreviation: string,
  mobileAbbreviation: string,
}

export interface ICountry {
  id: string,
  name: string,
  region?: IRegion,
  product?: IProduct,
}

export interface IRegion {
  id: string,
  name: string,
  displayName: string,
  abbreviation: string,
  priority: number,
  shortDisplayName: string,
  timeZoneId: string,
}

export interface IPrimaryIssuer {
  viewId: string,
  id: string,
  name: string,
  industryId?: string,
  subindustryId?: string,
  publicExchange?: string,
  publicTicker?: string,
  abbreviation?: string,
  issuerTicker: string,
}

export type { IIndustry };
export type { ISubindustry };

export interface IParent {
  viewId: string,
  id: string,
  name: string,
  industry: IIndustry,
  subindustry: ISubindustry,
  countDeals: number,
  publicExchange?: string,
  publicTicker?: string,
  abbreviation?: string,
  issuerTicker: string,
}

export interface IDealSize {
  [key: string]: ITrancheSize,
}

export interface ILead {
  viewId: number,
  id: string,
  name: string,
  industryId: string,
  subindustryId: string,
  publicExchange: string,
  publicTicker: string,
  abbreviation: string,
  issuerTicker: string,
}

export interface IJointLead {
  viewId: number,
  id: string,
  name: string,
  industryId: string,
  subindustryId: string,
  publicExchange: string,
  publicTicker: string,
  abbreviation: string,
  issuerTicker: string,
}

export enum DealType {
  SECTOR = 'sector',
  INDUSTRY = 'industry',
}

export enum DealStatus {
  Pulled = 'pulled',
  InMarket = 'inMarket',
  Priced = 'priced',
}

export interface IDealFiling {
  acceptanceDate: IDatetime,
  type: string,
  url: string,
}

export default interface IDeal {
  announcedDate?: null | IDatetime,
  collateral?: string | null,
  comments: string,
  country: ICountry,
  dealType: DealType,
  guarantor?: string | null,
  id: string,
  investorSetAvailable: boolean,
  jointLeads: IJointLead[],
  originator?: string | null,
  parent: IParent,
  pricingDate: IDatetime,
  primaryIssuer: IPrimaryIssuer,
  product: IProduct,
  riskRetention?: string | null,
  sector: ISector,
  seriesName: string,
  structuringLeads: ILead[],
  subsector: ISubsector,
  totalSize: IDealSize,
  tranches: ITranch[],
  useOfProceeds?: string | null,
  verified: boolean,
  viewId: number,
  volckerCompliance?: string | null,
  filings?: IDealFiling[] | null,
}

export interface IDealsPayload {
  page: number,
  perPage?: number,
  filters: {
    hasEitherPricingOrAnnouncedDate?: boolean,
    includedDealViewIds: string | number[],
    includedProductIds?: string,
    includedRegionIds?: string,
    excludedSectorIds?: string,
    includedSectorId?: string,
    includedSubindustryId?: string,
    includedStatusGroups?: DealStatus[],
    dateFrom?: string,
    dateTo?: string,
  },
  filterType: string,
}

export interface IDealsWithSubscriptionPayload {
  filterType: string,
  filters: {
    includedCompanyId?: string,
    includedCompanyRole?: string[],
    excludedSectorIds?: string[],
    includedProductIds?: string[],
    includedRegionIds?: string[],
    includedIndustryId?: string,
    includedSubindustryId?: string,
    hasEitherPricingOrAnnouncedDate: boolean,
    query: string[],
  },
  perPage: number,
  sortBy?: string,
  sortOrder?: string,
}

export interface IDealStatus {
  name: string,
  abbreviation: string,
}
